import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { SelectedFilterField } from '@cdw-ae/filter-bar-interfaces';
import {
  FilterBarParamStrategy,
  UrlStateSerializerService,
} from '@cdw-ae/url-params';
import {
  FilterBarId,
  ALL_FILTER_PREFIXES,
} from '../route-config/models/route-config.model';
import { RouteConfigService } from '../route-config/services/route-config.service';

/** EDUS-2575: the URL query param carrying a content-addressed filter snapshot. */
export const FILTER_SNAPSHOT_REF_PARAM = 'filterSnapshotRef';

@Injectable({
  providedIn: 'root',
})
export class UrlStateManagerService {
  constructor(
    private router: Router,
    private routeConfigService: RouteConfigService,
    private urlStateSerializer: UrlStateSerializerService,
    private filterStrategy: FilterBarParamStrategy
  ) {}

  deserializeFromUrl(
    route: string,
    queryParams: Record<string, string | string[]>
  ): { [filterBarId: string]: SelectedFilterField[] } {
    const allUrlConfigs = this.routeConfigService.getAllUrlParamConfigs(route);
    const allFilterBarConfigs =
      this.routeConfigService.getAllFilterBarConfigs(route);

    if (!(allUrlConfigs && allFilterBarConfigs)) {
      return {};
    }

    const filterBars: { [filterBarId: string]: SelectedFilterField[] } = {};

    Object.keys(allUrlConfigs).forEach((filterBarId) => {
      const urlParamConfig = allUrlConfigs[filterBarId as FilterBarId];
      const filterBarConfig = allFilterBarConfigs[filterBarId];

      if (
        !(urlParamConfig && filterBarConfig && queryParams) ||
        Object.keys(queryParams).length === 0
      ) {
        filterBars[filterBarId] = [];
        return;
      }

      const relevantParams = this.extractRelevantParams(
        queryParams,
        urlParamConfig.queryParamPrefix ?? ''
      );

      if (Object.keys(relevantParams).length === 0) {
        filterBars[filterBarId] = [];
        return;
      }

      const urlStateItems = this.urlStateSerializer.deserializeFromQueryParams(
        relevantParams,
        urlParamConfig
      );
      filterBars[filterBarId] = this.filterStrategy.convertToInput(
        urlStateItems,
        filterBarConfig
      );
    });

    return filterBars;
  }

  updateUrl(
    action: {
      filters?: SelectedFilterField[];
      filterBarId?: string;
      identifier?: string;
      clickedValue?: string;
    },
    currentRoute: string
  ): { filters: SelectedFilterField[]; filterBarId: string } | null {
    const { filters, filterBarId, identifier, clickedValue } = action;

    // Handle navigation if identifier and clickedValue are provided
    const navigationResult = this.handleNavigation(
      identifier,
      clickedValue,
      currentRoute
    );

    const filtersToUse = this.mergeFilters(
      filters || [],
      navigationResult?.filters || []
    );

    const targetRoute = navigationResult?.targetRoute || currentRoute;
    const targetFilterBarId = filterBarId || navigationResult?.filterBarId;
    const workflowType = navigationResult?.workflowType;

    this.navigateWithFilters(
      filtersToUse,
      targetRoute,
      currentRoute,
      targetFilterBarId ?? '',
      workflowType
    );

    // Return computed filters so the caller can update the store
    // before the async navigation completes
    if (targetFilterBarId) {
      return { filters: filtersToUse, filterBarId: targetFilterBarId };
    }
    return null;
  }

  /**
   * Write (or clear, with null) the `filterSnapshotRef` URL param for a filter
   * bar (EDUS-2575). When set, the bar's inline filter param is dropped so the
   * shared address-bar URL stays small; when null, the ref param is removed and
   * the inline filters (already written by navigateWithFilters) remain.
   *
   * Uses `replaceUrl: true` so this swap REPLACES the current history entry
   * rather than pushing a new one — otherwise the large inline `?<prefix>=…`
   * URL that navigateWithFilters just wrote would linger in the back-stack.
   * (`queryParamsHandling` only controls param merge vs. replace, not history.)
   *
   * Returns the `router.navigate` promise (`true` when a navigation occurred,
   * `false` when the router ignored it — e.g. the URL was unchanged or a guard
   * blocked it) so the caller can tell whether a `ROUTER_NAVIGATED` will follow.
   */
  writeSnapshotRefParam(
    filterBarId: string,
    ref: string | null
  ): Promise<boolean> {
    const route = this.router.url.split('?')[0];
    const config = this.routeConfigService.getUrlParamConfig(
      route,
      filterBarId as FilterBarId
    );
    const prefix = config?.queryParamPrefix;
    const currentParams = {
      ...this.router.routerState.root.snapshot.queryParams,
    };
    if (ref) {
      if (prefix) {
        delete currentParams[prefix];
      }
      currentParams[FILTER_SNAPSHOT_REF_PARAM] = ref;
    } else {
      delete currentParams[FILTER_SNAPSHOT_REF_PARAM];
    }
    return this.router.navigate([], {
      queryParams: currentParams,
      queryParamsHandling: 'replace' as const,
      replaceUrl: true,
    });
  }

  private extractRelevantParams(
    queryParams: Record<string, unknown>,
    prefix: string
  ): Record<string, unknown> {
    const relevantParams: Record<string, unknown> = {};
    if (queryParams[prefix]) {
      relevantParams[prefix] = queryParams[prefix];
    }
    return relevantParams;
  }

  private mergeFilters(
    currentFilters: SelectedFilterField[],
    navigationFilters: SelectedFilterField[]
  ): SelectedFilterField[] {
    if (!navigationFilters.length) return currentFilters;
    if (!currentFilters.length) return navigationFilters;

    // Keep current filters and only add navigation filters for fields that don't exist
    const currentFieldNames = new Set(currentFilters.map((f) => f.field.name));
    return [
      ...currentFilters,
      ...navigationFilters.filter((f) => !currentFieldNames.has(f.field.name)),
    ];
  }

  private handleNavigation(
    identifier?: string,
    clickedValue?: string,
    currentRoute?: string
  ) {
    if (!(identifier && currentRoute)) return null;

    const navigationConfig =
      this.routeConfigService.getNavigationConfig(currentRoute);
    const cardConfig = navigationConfig?.find(
      (c: { identifier: string }) => c.identifier === identifier
    );

    if (!cardConfig) return null;

    const targetRoute = cardConfig.targetRoute;
    const workflowType = cardConfig.workflowType;

    if (!(cardConfig.filterMappings && clickedValue)) {
      return { filters: [], targetRoute, workflowType };
    }

    const filterBarId = cardConfig?.targetFilterBarId || null;

    const mapping = cardConfig.filterMappings.find(
      (m: { clickedValue: string }) => m.clickedValue === clickedValue
    );
    if (!(mapping && filterBarId)) {
      return { filters: [], targetRoute, filterBarId, workflowType };
    }

    const filterBarFields = this.routeConfigService.getFilterBarConfig(
      targetRoute,
      filterBarId
    );
    if (!filterBarFields) return null;

    const filters = this.filterStrategy.convertToInput(
      mapping.filters,
      filterBarFields
    );

    return { filters, targetRoute, filterBarId, workflowType };
  }

  private navigateWithFilters(
    filters: SelectedFilterField[],
    targetRoute: string,
    currentRoute: string,
    filterBarId: string,
    workflowType?: string
  ): void {
    const urlStateItems = this.filterStrategy.convertFromInput(filters);
    const configToUse = this.routeConfigService.getUrlParamConfig(
      targetRoute,
      filterBarId as FilterBarId
    );
    const navigationTarget = targetRoute !== currentRoute ? [targetRoute] : [];

    if (!configToUse) {
      const extras = workflowType ? { state: { workflowType } } : {};
      this.router.navigate(navigationTarget, extras);
      return;
    }

    const newParams = this.urlStateSerializer.serializeToQueryParams(
      urlStateItems,
      configToUse
    );

    // When navigating to a different route, clean up old filter params from other routes
    // When staying on same route, clean up current filter prefix if filters are empty
    const isChangingRoute = targetRoute !== currentRoute;
    const currentParams = {
      ...this.router.routerState.root.snapshot.queryParams,
    };

    // Filters are being re-inlined here, so any stale snapshot ref must drop
    // too (EDUS-2575). It is re-added by writeSnapshotRefParam once the
    // data-fetch path computes the new ref.
    delete currentParams[FILTER_SNAPSHOT_REF_PARAM];

    if (isChangingRoute) {
      // Remove all filter-related query params when changing routes
      ALL_FILTER_PREFIXES.forEach((prefix) => {
        delete currentParams[prefix];
      });
    } else if (
      Object.keys(newParams).length === 0 &&
      configToUse?.queryParamPrefix
    ) {
      // Same route: if newParams is empty, explicitly remove the current filter prefix
      delete currentParams[configToUse.queryParamPrefix];
    }

    const queryParams = { ...currentParams, ...newParams };

    const extras = workflowType
      ? {
          queryParams,
          queryParamsHandling: 'replace' as const,
          state: { workflowType },
        }
      : { queryParams, queryParamsHandling: 'replace' as const };

    this.router.navigate(navigationTarget, extras);
  }
}
